import { useState } from 'react';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      return;
    }

    try {
      const formUrl = 'https://readdy.ai/api/form/d9q12sknlsngrm4llpog';
      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'phone_alt')
            .map(([key, value]) => [key, String(value)])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText;
        setFormError(String(serverMsg || '提交失败，请重试'));
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  const canonicalUrl = 'https://www.aiqingdao.com/about';

  return (
    <>
      <SEOHead
        title="关于我们 - 爱情岛视频 | 品牌介绍与联系方式"
        description="了解爱情岛视频的品牌故事、发展历程与口碑评价。爱情岛视频致力于提供最新、最全、高清的爱情岛视频资源，免费在线观看。联系我们获取更多信息。"
        keywords="爱情岛视频,关于爱情岛视频,爱情岛视频品牌,爱情岛视频联系方式"
        canonicalUrl={canonicalUrl}
      />
      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.aiqingdao.com/' },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: canonicalUrl },
        ],
      }} />
      <SchemaOrg type="Organization" data={{
        name: '爱情岛视频',
        url: canonicalUrl,
        description: '爱情岛视频高清在线观看入口，提供最新爱情岛视频资源',
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer service',
          email: 'contact@aiqingdao.com',
        },
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb items={[{ label: '关于我们' }]} />

          {/* Brand Introduction */}
          <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
            <div className="max-w-[1000px] mx-auto">
              <div className="text-center mb-10">
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-4">
                  关于爱情岛视频
                </h1>
                <p className="text-foreground-500 max-w-2xl mx-auto text-sm md:text-base">
                  致力于为观众提供最新、最全、高清的爱情岛视频资源
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center mb-12">
                <div>
                  <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-4">
                    品牌故事
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    <strong>爱情岛视频</strong>成立于2020年，专注于为国内观众提供高质量的爱情岛系列视频资源。作为领先的<strong>爱情岛视频在线观看</strong>平台，我们始终坚持"免费、高清、流畅"的服务理念，让每一位观众都能轻松享受精彩内容。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    经过多年的发展，<strong>爱情岛视频</strong>已经积累了超过500部<strong>爱情岛视频全集</strong>资源，涵盖全集、花絮、集锦、访谈等多种内容类型。我们的<strong>爱情岛视频高清</strong>专区更是以4K超清画质赢得了观众的广泛好评。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    如今，<strong>爱情岛视频免费播放</strong>服务已覆盖全国数百万用户，每周保持5期以上的内容更新频率。<strong>爱情岛视频最新资源</strong>总是能在第一时间为观众呈现最精彩的视频内容。
                  </p>
                </div>
                <div className="rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20warm%20and%20inviting%20creative%20studio%20workspace%20with%20large%20monitors%20displaying%20video%20editing%20timeline%2C%20plants%2C%20cozy%20lighting%2C%20modern%20minimalist%20design%2C%20professional%20yet%20homely%20atmosphere%2C%20warm%20rose%20and%20cream%20tones&width=800&height=500&seq=about-brand-01&orientation=landscape"
                    alt="爱情岛视频品牌故事"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="w-full px-4 md:px-6 py-12 bg-background-100">
            <div className="max-w-[1000px] mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                {[
                  { value: '500+', label: '视频资源' },
                  { value: '200万+', label: '活跃用户' },
                  { value: '98%', label: '用户好评率' },
                  { value: '5年', label: '运营经验' },
                ].map((stat, i) => (
                  <div key={i}>
                    <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500">
                      {stat.value}
                    </div>
                    <div className="text-xs md:text-sm text-foreground-500 mt-2">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Reputation */}
          <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
            <div className="max-w-[1000px] mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-10">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {[
                  {
                    name: '小王',
                    text: '一直在爱情岛视频看最新更新，画质清晰流畅，更新速度也很快，强烈推荐！免费播放真的太良心了。',
                    rating: 5,
                  },
                  {
                    name: 'Linda',
                    text: '爱情岛视频高清专区真的是视觉享受，4K画质看着特别舒服。全集资源也很全，想看的都能找到。',
                    rating: 5,
                  },
                  {
                    name: '阿杰',
                    text: '分类做得很清晰，想找什么类型的都能快速定位。每周都有新内容，已经成了我每周必看的视频平台。',
                    rating: 5,
                  },
                ].map((review, i) => (
                  <div
                    key={i}
                    className="bg-background-100 rounded-lg p-5 border border-background-200/70"
                  >
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: review.rating }).map((_, j) => (
                        <div key={j} className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-star-fill text-accent-500 text-sm"></i>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      {review.text}
                    </p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                        <span className="text-xs font-medium text-primary-600">
                          {review.name.charAt(0)}
                        </span>
                      </div>
                      <span className="text-sm font-medium text-foreground-800">{review.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact + FAQ */}
          <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
            <div className="max-w-[1000px] mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div>
                  <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">
                    联系我们
                  </h2>
                  <div className="space-y-4 mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-primary-50 rounded-lg">
                        <i className="ri-mail-line text-primary-500"></i>
                      </div>
                      <div>
                        <div className="text-xs text-foreground-500">邮箱</div>
                        <div className="text-sm font-medium text-foreground-900">contact@aiqingdao.com</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-primary-50 rounded-lg">
                        <i className="ri-time-line text-primary-500"></i>
                      </div>
                      <div>
                        <div className="text-xs text-foreground-500">工作时间</div>
                        <div className="text-sm font-medium text-foreground-900">周一至周日 9:00 - 22:00</div>
                      </div>
                    </div>
                  </div>

                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form="true"
                    className="space-y-4"
                  >
                    <div className="hp-field-wrapper">
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                        placeholder="请输入您的留言（最多500字）"
                      />
                    </div>
                    <button
                      type="submit"
                      className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap"
                    >
                      发送留言
                    </button>

                    {formStatus === 'success' && (
                      <p className="text-sm text-green-600 bg-green-50 px-4 py-2 rounded-lg">
                        留言发送成功！我们会尽快回复您。
                      </p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-600 bg-red-50 px-4 py-2 rounded-lg">
                        {formError}
                      </p>
                    )}
                  </form>
                </div>

                <div>
                  <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">
                    常见问题
                  </h2>
                  <div className="space-y-3">
                    {[
                      { q: '爱情岛视频如何观看？', a: '直接访问我们的网站即可免费在线观看所有爱情岛视频，无需注册和下载。' },
                      { q: '视频支持哪些清晰度？', a: '支持从标清到4K超清多种画质，系统会根据您的网络自动选择最佳画质。' },
                      { q: '更新频率如何？', a: '我们保持每周5期以上的更新频率，第一时间提供最新的爱情岛视频资源。' },
                    ].map((item, i) => (
                      <div key={i} className="bg-background-50 rounded-lg p-4 border border-background-200/70">
                        <h4 className="text-sm font-semibold text-foreground-900 mb-2">
                          <a id={`faq-${i}`} className="no-underline">{item.q}</a>
                        </h4>
                        <p className="text-xs text-foreground-600 leading-relaxed">{item.a}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}